// Móveis das áreas comuns (copa, lounge, banheiro, reunião, recepção, servidores, terraço).
// 'sofa' é o gabarito da Onda 0; o resto do lote segue o mesmo padrão: um Pix por móvel,
// letras de paleta locais, luz do alto-esquerda (tom 3 em cima/esquerda, tom 1 embaixo/direita)
// e contorno com o tom 0 do material.

import { Pix } from '../pix.js';
import type { FurnitureDef } from '../types.js';

/** Sofá de 2 lugares, visto de frente: encosto, almofadas, braços. */
function sofa(): Pix {
  const pix = new Pix(32, 24);
  pix.box(2, 0, 28, 11, { fill: 'c', light: 'l', dark: 'd' }); // encosto
  pix.vline(16, 2, 8, 'd'); // divisão do encosto
  pix.box(3, 11, 13, 7, { fill: 'c', light: 'l', dark: 'd' }); // almofada esquerda
  pix.box(16, 11, 13, 7, { fill: 'c', light: 'l', dark: 'd' }); // almofada direita
  pix.box(0, 6, 4, 14, { fill: 'c', light: 'l', dark: 'd' }); // braço esquerdo
  pix.box(28, 6, 4, 14, { fill: 'c', light: 'l', dark: 'd' }); // braço direito
  pix.rect(3, 18, 26, 2, 'd'); // frente
  pix.rect(2, 20, 2, 3, 'w').rect(28, 20, 2, 3, 'w'); // pés
  return pix.outline('o');
}

/** Sofá de 3 lugares, visto de frente. Mesma família do sofá de 2, com um lugar a mais. */
function sofaTres(): Pix {
  const pix = new Pix(48, 24);
  pix.box(2, 0, 44, 11, { fill: 'c', light: 'l', dark: 'd' }); // encosto
  pix.vline(16, 2, 8, 'd').vline(31, 2, 8, 'd'); // divisões do encosto
  pix.box(3, 11, 14, 7, { fill: 'c', light: 'l', dark: 'd' }); // almofadas
  pix.box(17, 11, 14, 7, { fill: 'c', light: 'l', dark: 'd' });
  pix.box(31, 11, 14, 7, { fill: 'c', light: 'l', dark: 'd' });
  pix.box(0, 6, 4, 14, { fill: 'c', light: 'l', dark: 'd' }); // braços
  pix.box(44, 6, 4, 14, { fill: 'c', light: 'l', dark: 'd' });
  pix.rect(3, 18, 42, 2, 'd'); // frente
  pix.rect(2, 20, 2, 3, 'w').rect(44, 20, 2, 3, 'w'); // pés
  return pix.outline('o');
}

// ---------------------------------------------------------------- copa

const BALCAO_PALETTE = { o: 'wood0', W: 'wood2', L: 'wood3', D: 'wood1', T: 'concrete2', U: 'concrete3', V: 'concrete1', M: 'metal1', N: 'metal2', K: 'metal3' };

/** Balcão com pia: armário de madeira, tampo de pedra e cuba de inox. */
function balcaoPia(): Pix {
  const pix = new Pix(32, 22);
  pix.box(0, 9, 32, 13, { fill: 'W', light: 'L', dark: 'D' }); // armário
  pix.vline(15, 11, 10, 'D'); // divisão das portas
  pix.box(0, 3, 32, 6, { fill: 'T', light: 'U', dark: 'V' }); // tampo de pedra
  pix.box(5, 4, 12, 4, { fill: 'M', light: 'N', dark: 'N' }); // cuba
  pix.vline(10, 0, 4, 'K'); // torneira
  return pix.outline('o');
}

const CAFETEIRA_PALETTE = { o: 'metal0', m: 'metal2', l: 'metal3', d: 'metal1', k: 'black1', g: 'leaf3', p: 'pot2', P: 'pot3', q: 'pot1', v: 'glass2' };

/** Cafeteira de balcão: corpo de metal, visor com luzinha, bule de café. O vapor muda entre os quadros. */
function cafeteira(quadro: 0 | 1): Pix {
  const pix = new Pix(16, 22);
  pix.box(2, 6, 12, 13, { fill: 'm', light: 'l', dark: 'd' }); // corpo
  pix.rect(4, 8, 8, 4, 'k').px(5, 9, 'g').px(7, 9, 'g'); // visor com luzinha
  pix.box(10, 14, 4, 4, { fill: 'p', light: 'P', dark: 'q' }); // bule de café
  pix.hline(3, 19, 10, 'd'); // bandeja
  pix.outline('o');
  // vapor por cima do contorno, para não ganhar borda
  if (quadro === 0) pix.px(6, 4, 'v').px(7, 2, 'v').px(6, 0, 'v');
  else pix.px(8, 3, 'v').px(9, 1, 'v').px(8, 0, 'v');
  return pix;
}

/** Geladeira dupla porta, de frente: corpo claro, linha do freezer e puxadores. */
function geladeira(): Pix {
  const pix = new Pix(16, 30);
  pix.box(0, 0, 16, 30, { fill: 'p', light: 'q', dark: 'r' }); // corpo
  pix.hline(0, 10, 16, 'r'); // divisão freezer e geladeira
  pix.vline(13, 3, 5, 'k').vline(13, 13, 6, 'k'); // puxadores
  return pix.outline('o');
}

/** Micro-ondas em cima do balcão: caixa clara, porta com vidro escuro e painel de botões. */
function microondas(): Pix {
  const pix = new Pix(16, 12);
  pix.box(0, 1, 16, 11, { fill: 'p', light: 'q', dark: 'r' }); // corpo
  pix.box(2, 3, 9, 7, { fill: 'k', light: 'K', dark: 'k' }); // porta com vidro
  pix.vline(13, 3, 7, 'k').px(13, 4, 'g').px(13, 7, 'g'); // painel
  return pix.outline('o');
}

/** Máquina de snacks: gabinete vermelho, vitrine com produtos e teclado. Brilha à noite. */
function maquinaSnacks(): Pix {
  const pix = new Pix(16, 30);
  pix.box(0, 0, 16, 30, { fill: 'R', light: 'S', dark: 'D' }); // gabinete
  pix.box(2, 3, 12, 17, { fill: 'g', light: 'G', dark: 'h' }); // vitrine
  pix.hline(3, 7, 10, 'y').hline(3, 13, 10, 'b').hline(3, 18, 10, 'w'); // prateleiras de produtos
  pix.rect(4, 5, 2, 2, 'w').rect(8, 11, 2, 2, 'y').rect(5, 16, 3, 2, 'b'); // pacotes
  pix.box(10, 22, 4, 6, { fill: 'k', light: 'K', dark: 'k' }); // teclado
  pix.rect(3, 22, 5, 2, 'K').px(4, 25, 'g'); // entrega e moeda
  return pix.outline('o');
}

/** Elipse cheia, achatada na vertical: é como um círculo aparece na vista de 3/4 de cima. */
function elipse(pix: Pix, cx: number, cy: number, rx: number, ry: number, c: string): void {
  for (let y = 0; y < pix.h; y++) {
    for (let x = 0; x < pix.w; x++) {
      if (((x + 0.5 - cx) / rx) ** 2 + ((y + 0.5 - cy) / ry) ** 2 <= 1) pix.px(x, y, c);
    }
  }
}

/** Mesa redonda de bistrô: tampo elíptico de madeira (vista de 3/4) sobre pedestal. Ocupa 2×2 tiles. */
function mesaRedonda(): Pix {
  const pix = new Pix(32, 30);
  elipse(pix, 16, 12, 15, 9, 'D'); // borda (espessura do tampo)
  elipse(pix, 16, 11, 15, 8, 'W'); // tampo
  elipse(pix, 13, 9, 7, 3.5, 'L'); // brilho no topo à esquerda
  pix.rect(14, 19, 4, 7, 'D'); // pedestal
  pix.rect(9, 26, 14, 2, 'D'); // base
  return pix.outline('o');
}

/** Banqueta alta de bar: assento redondo estofado e coluna de metal. */
function banqueta(): Pix {
  const pix = new Pix(14, 20);
  pix.disc(7, 6, 6, 'd').disc(7, 5, 5, 'c').disc(5, 3, 2, 'l'); // assento
  pix.vline(6, 11, 6, 'm').vline(7, 11, 6, 'n'); // coluna
  pix.hline(2, 17, 10, 'm').hline(3, 18, 8, 'n'); // base
  return pix.outline('o');
}

/** Cadeira de madeira de jantar, de frente: encosto com ripas, assento e pernas. */
function cadeiraMadeira(): Pix {
  const pix = new Pix(16, 22);
  pix.box(3, 0, 10, 10, { fill: 'W', light: 'L', dark: 'D' }); // encosto
  pix.hline(5, 3, 6, 'D').hline(5, 6, 6, 'D'); // ripas
  pix.box(1, 10, 14, 4, { fill: 'W', light: 'L', dark: 'D' }); // assento
  pix.rect(2, 14, 2, 8, 'D').rect(12, 14, 2, 8, 'D'); // pernas
  return pix.outline('o');
}

/** Mesa de café retangular de madeira, com pernas de canto. Ocupa 3×1 tiles. */
function mesaCafe(): Pix {
  const pix = new Pix(48, 22);
  pix.box(0, 4, 48, 10, { fill: 'W', light: 'L', dark: 'D' }); // tampo
  pix.rect(0, 14, 48, 2, 'D'); // faixa da frente
  pix.rect(4, 16, 3, 6, 'D').rect(41, 16, 3, 6, 'D'); // pernas
  return pix.outline('o');
}

// ---------------------------------------------------------------- lounge

/** Tapete liso com faixa de borda e um fio de padrão no meio. Chão: não bloqueia a caminhada. */
function tapete(): Pix {
  const pix = new Pix(48, 32);
  pix.rect(0, 0, 48, 32, 'b'); // borda
  pix.rect(2, 2, 44, 28, 'c'); // miolo
  pix.hline(4, 4, 40, 'l').hline(4, 27, 40, 'l'); // filetes
  pix.pattern(6, 15, 36, 'd.l.');
  return pix;
}

/** Poltrona de tecido, de frente: encosto alto, braços e assento. */
function poltrona(): Pix {
  const pix = new Pix(16, 22);
  pix.box(2, 0, 12, 12, { fill: 'c', light: 'l', dark: 'd' }); // encosto
  pix.box(0, 8, 4, 10, { fill: 'c', light: 'l', dark: 'd' }); // braço esquerdo
  pix.box(12, 8, 4, 10, { fill: 'c', light: 'l', dark: 'd' }); // braço direito
  pix.box(4, 12, 8, 5, { fill: 'c', light: 'l', dark: 'd' }); // assento
  pix.rect(4, 17, 8, 2, 'd'); // frente do assento
  pix.rect(3, 19, 2, 2, 'k').rect(11, 19, 2, 2, 'k'); // pés
  return pix.outline('o');
}

/** Pufe redondo de tecido. */
function pufe(): Pix {
  const pix = new Pix(16, 14);
  elipse(pix, 8, 8, 7, 5.5, 'd');
  elipse(pix, 8, 7, 7, 4.8, 'c');
  elipse(pix, 6, 5, 2.5, 1.2, 'l');
  return pix.outline('o');
}

/** Mesa de centro de vidro com pés de madeira e uma revista em cima. Ocupa 2×1 tiles. */
function mesaCentro(): Pix {
  const pix = new Pix(32, 14);
  pix.box(0, 2, 32, 8, { fill: 'G', light: 'g', dark: 'h' }); // tampo de vidro
  pix.rect(20, 4, 5, 3, 'p').hline(20, 4, 5, 'q'); // revista
  pix.rect(3, 10, 2, 4, 'D').rect(27, 10, 2, 4, 'D'); // pés
  return pix.outline('o');
}

/** Mesa de ping-pong: tampo verde com linhas brancas e rede no meio. Ocupa 3×2 tiles. */
function pingPong(): Pix {
  const pix = new Pix(48, 28);
  pix.box(0, 4, 48, 16, { fill: 'G', light: 'g', dark: 'h' }); // tampo
  pix.hline(2, 6, 44, 'w').hline(2, 17, 44, 'w'); // linhas laterais
  pix.vline(24, 4, 16, 'n'); // rede
  pix.rect(4, 20, 3, 8, 'd').rect(41, 20, 3, 8, 'd'); // pés
  return pix.outline('o');
}

/** Fliperama de pé: gabinete preto, marquise dourada e tela (quadros animam os pontinhos). */
function fliperama(quadro: 0 | 1): Pix {
  const pix = new Pix(16, 30);
  pix.box(1, 0, 14, 30, { fill: 'k', light: 'K', dark: 'j' }); // gabinete
  pix.box(2, 1, 12, 5, { fill: 'y', light: 'Y', dark: 'z' }); // marquise
  pix.box(3, 8, 10, 9, { fill: 'b', light: 'B', dark: 'b' }); // tela
  pix.outline('o');
  pix.px(5 + quadro, 11, 'c').px(9 - quadro, 14, 'c'); // pontinhos da tela
  pix.rect(3, 19, 10, 3, 'j'); // painel de controle
  pix.px(5, 20, 'r').px(9, 20, 'g'); // botões
  return pix;
}

/** Rack de TV: TV de pé sobre o móvel de madeira com console. Ocupa 2×1 tiles. */
function rackTv(): Pix {
  const pix = new Pix(32, 28);
  pix.box(5, 0, 22, 15, { fill: 'k', light: 'K', dark: 'j' }); // TV
  pix.box(7, 2, 18, 11, { fill: 's', light: 'S', dark: 'r' }); // tela
  pix.rect(14, 15, 4, 2, 'm'); // pé
  pix.box(0, 17, 32, 11, { fill: 'W', light: 'L', dark: 'D' }); // móvel
  pix.hline(0, 22, 32, 'D'); // prateleira
  pix.rect(4, 18, 9, 3, 'k').rect(19, 18, 8, 3, 'k'); // consoles
  return pix.outline('o');
}

/** Estante de jogos: quatro prateleiras com caixas de cores diferentes. */
function estanteGames(): Pix {
  const pix = new Pix(16, 32);
  pix.box(0, 0, 16, 32, { fill: 'W', light: 'L', dark: 'D' }); // gabinete
  for (const y of [8, 16, 24]) pix.hline(1, y, 14, 'D'); // prateleiras
  const linhas: Array<[number, string[]]> = [
    [1, ['r', 'b', 'g', 'y']],
    [9, ['b', 'y', 'p', 'r']],
    [17, ['g', 'p', 'r', 'b']],
    [25, ['y', 'r', 'b', 'g']],
  ];
  for (const [y, cores] of linhas) {
    pix.rect(2, y, 2, 6, cores[0]!).rect(5, y, 2, 6, cores[1]!).rect(8, y, 3, 6, cores[2]!).rect(12, y, 2, 6, cores[3]!);
  }
  return pix.outline('o');
}

const SOFA_VERDE = { o: 'fabricGreen0', d: 'fabricGreen1', c: 'fabricGreen2', l: 'fabricGreen3', w: 'wood1' };

// ---------------------------------------------------------------- banheiro

/** Vaso sanitário visto de frente-cima: caixa da descarga atrás e bacia oval à frente. */
function vasoSanitario(): Pix {
  const pix = new Pix(14, 20);
  pix.box(3, 0, 8, 7, { fill: 'w', light: 'x', dark: 'y' }); // caixa da descarga
  pix.box(1, 7, 12, 13, { fill: 'w', light: 'x', dark: 'y' }); // bacia
  pix.rect(4, 11, 6, 4, 'k'); // água
  return pix.outline('o');
}

/** Pia de banheiro: cuba branca sobre coluna, torneira atrás. */
function piaBanheiro(): Pix {
  const pix = new Pix(16, 22);
  pix.rect(7, 0, 2, 4, 'm'); // torneira
  pix.box(1, 4, 14, 8, { fill: 'w', light: 'x', dark: 'y' }); // cuba
  pix.rect(6, 6, 4, 2, 'k'); // ralo
  pix.rect(5, 12, 6, 10, 'y'); // coluna
  return pix.outline('o');
}

/** Divisória de cabine: painel alto de plástico com fresta da porta e trinco. */
function divisoriaCabine(): Pix {
  const pix = new Pix(16, 28);
  pix.box(0, 0, 16, 28, { fill: 'p', light: 'q', dark: 'r' }); // painel
  pix.vline(11, 4, 22, 'r'); // fresta da porta
  pix.px(12, 14, 'm'); // trinco
  return pix.outline('o');
}

/** Espelho de banheiro: moldura de metal e vidro com reflexo. Vai na parede. */
function espelhoBanheiro(): Pix {
  const pix = new Pix(16, 14);
  pix.box(0, 0, 16, 14, { fill: 'm', light: 'l', dark: 'd' }); // moldura
  pix.box(2, 2, 12, 10, { fill: 'g', light: 'G', dark: 'h' }); // espelho
  pix.hline(4, 4, 3, 'G').px(4, 5, 'G'); // reflexo
  return pix.outline('o');
}

/** Secador de mãos: caixa branca com bocal embaixo. Vai na parede. */
function secadorMaos(): Pix {
  const pix = new Pix(10, 14);
  pix.box(0, 0, 10, 8, { fill: 'p', light: 'q', dark: 'r' }); // caixa
  pix.box(2, 8, 6, 6, { fill: 'r', light: 'p', dark: 'r' }); // bocal
  pix.px(4, 3, 'k'); // botão
  return pix.outline('o');
}

// ---------------------------------------------------------------- reunião

/** Mesa de reunião comprida: tampo de carvalho com reflexo, borda frontal escura e pés visíveis. Notebooks, papéis e copos em cima. Ocupa 4×2 tiles. */
function mesaReuniao(): Pix {
  const pix = new Pix(64, 30);
  pix.box(0, 0, 64, 18, { fill: 'W', light: 'L', dark: 'D' }); // tampo visto de cima
  pix.hline(2, 9, 60, 'D').hline(14, 14, 26, 'D'); // veio da madeira
  pix.hline(4, 4, 18, 'L').hline(40, 12, 14, 'L'); // reflexo no verniz
  pix.rect(0, 18, 64, 3, 'F'); // borda frontal, em sombra
  for (const x of [3, 19, 43, 59]) pix.box(x, 21, 3, 8, { fill: 'F', light: 'D', dark: 'G' }); // pés
  // notebook de metal fechado, notebook preto e pilha de papéis
  pix.box(5, 6, 13, 7, { fill: 'm', light: 'l', dark: 'd' }).px(10, 9, 'l');
  pix.box(44, 5, 14, 7, { fill: 'k', light: 'K', dark: 'j' }).hline(46, 8, 10, 'j');
  pix.box(22, 7, 9, 5, { fill: 'q', light: 'p', dark: 'r' }).hline(24, 9, 5, 'z').hline(24, 10, 4, 'z');
  // dois copos de café
  pix.box(35, 9, 3, 4, { fill: 'c', light: 'C', dark: 'y' }).hline(35, 9, 3, 'T');
  pix.box(54, 10, 3, 4, { fill: 'c', light: 'C', dark: 'y' }).hline(54, 10, 3, 'T');
  return pix.outline('o');
}

/** Cadeira de reunião estofada, de frente: encosto, assento e base de metal. */
function cadeiraReuniao(): Pix {
  const pix = new Pix(16, 22);
  pix.box(2, 0, 12, 10, { fill: 'c', light: 'l', dark: 'd' }); // encosto
  pix.box(1, 10, 14, 5, { fill: 'c', light: 'l', dark: 'd' }); // assento
  pix.rect(7, 15, 2, 3, 'm'); // coluna
  pix.hline(3, 18, 10, 'm').px(2, 19, 'n').px(13, 19, 'n'); // base em estrela
  return pix.outline('o');
}

/** Tela de projeção recolhida na parede: caixa do rolo em cima e tela branca embaixo. Ocupa 2×1 tiles. */
function telaProjecao(): Pix {
  const pix = new Pix(32, 16);
  pix.box(0, 0, 32, 3, { fill: 'm', light: 'l', dark: 'd' }); // caixa do rolo
  pix.box(2, 3, 28, 12, { fill: 'w', light: 'x', dark: 'y' }); // tela
  return pix.outline('o');
}

// ---------------------------------------------------------------- recepção

/** Balcão de recepção, de frente: tampo de madeira clara e painel vermelho com faixa dourada. Ocupa 3×1 tiles. */
function balcaoRecepcao(): Pix {
  const pix = new Pix(48, 26);
  pix.box(0, 3, 48, 7, { fill: 'W', light: 'L', dark: 'D' }); // tampo (visto de cima)
  pix.box(0, 10, 48, 16, { fill: 'R', light: 'S', dark: 'Q' }); // painel da frente
  pix.hline(0, 14, 48, 'G'); // faixa dourada
  pix.rect(21, 18, 6, 5, 'G'); // placa com a marca
  return pix.outline('o');
}

/** Totem de recepção: placa vermelha com a marca, tela e base de metal. */
function totem(): Pix {
  const pix = new Pix(16, 32);
  pix.box(2, 0, 12, 6, { fill: 'R', light: 'S', dark: 'D' }); // placa vermelha
  pix.rect(6, 2, 4, 3, 'W'); // marca
  pix.box(2, 6, 12, 16, { fill: 'p', light: 'q', dark: 'r' }); // painel
  pix.box(4, 8, 8, 10, { fill: 'b', light: 'B', dark: 'b' }); // tela
  pix.rect(5, 10, 6, 1, 'c').rect(5, 13, 4, 1, 'c'); // linhas de texto
  pix.box(3, 22, 10, 6, { fill: 'm', light: 'l', dark: 'd' }); // corpo de metal
  pix.rect(1, 28, 14, 3, 'd'); // base
  return pix.outline('o');
}

// ---------------------------------------------------------------- servidores

/** Rack de servidor: gabinete preto com unidades de 1U. Os LEDs trocam de cor entre os quadros. */
function rack(quadro: 0 | 1): Pix {
  const pix = new Pix(16, 36);
  pix.box(0, 0, 16, 36, { fill: 'k', light: 'K', dark: 'j' }); // gabinete
  for (let i = 0; i < 5; i++) {
    const y = 2 + i * 7; // cada unidade: 6 px de painel e 1 px de vão
    pix.box(2, y, 12, 6, { fill: 'f', light: 'F', dark: 'j' }); // painel da unidade
    pix.hline(4, y + 2, 6, 'j'); // grade de ventilação
    pix.px(11, y + 1, (i + quadro) % 2 ? 'r' : 'g'); // LED de atividade
    pix.px(11, y + 4, (i + quadro) % 2 ? 'g' : 'b'); // LED de rede
  }
  return pix;
}

/** No-break de chão: caixa de plástico com um painel e LEDs. */
function noBreak(): Pix {
  const pix = new Pix(16, 12);
  pix.box(0, 2, 16, 10, { fill: 'p', light: 'q', dark: 'r' }); // caixa
  pix.rect(2, 5, 8, 2, 'k'); // painel
  pix.px(12, 5, 'g').px(13, 5, 'g'); // LEDs
  return pix.outline('o');
}

/** Mesa de NOC: dois monitores com gráficos e teclado na frente. Ocupa 2×1 tiles. */
function mesaNoc(): Pix {
  const pix = new Pix(32, 26);
  pix.box(1, 0, 14, 11, { fill: 'k', light: 'K', dark: 'j' }); // monitor esquerdo
  pix.rect(3, 2, 10, 7, 's').hline(4, 7, 8, 'c').vline(6, 3, 4, 'c'); // tela com gráfico
  pix.box(17, 0, 14, 11, { fill: 'k', light: 'K', dark: 'j' }); // monitor direito
  pix.rect(19, 2, 10, 7, 's').hline(20, 3, 8, 'c').vline(26, 3, 4, 'c'); // tela com gráfico
  pix.rect(14, 9, 4, 2, 'm'); // suporte
  pix.box(0, 13, 32, 5, { fill: 'W', light: 'L', dark: 'D' }); // tampo
  pix.rect(9, 14, 14, 2, 'k'); // teclado
  pix.rect(2, 18, 2, 8, 'D').rect(28, 18, 2, 8, 'D'); // pés
  return pix.outline('o');
}

// ---------------------------------------------------------------- terraço e hall

/** Espreguiçadeira vista de cima, com a cabeça no alto: estrutura de madeira, tecido listrado e toalha no assento. Cabe em 1 tile. */
function espreguicadeira(): Pix {
  const pix = new Pix(16, 20);
  pix.box(1, 0, 14, 16, { fill: 'D', light: 'L', dark: 'G' }); // estrutura de madeira
  for (let x = 3; x < 13; x++) pix.vline(x, 2, 12, ((x - 3) >> 1) % 2 ? 'w' : 'c'); // listras de 2 px
  pix.hline(3, 2, 10, 'l'); // luz no topo do encosto
  pix.box(4, 9, 8, 6, { fill: 'Y', light: 'Z', dark: 'y' }); // toalha sobre o assento
  pix.hline(4, 15, 8, 'y').px(5, 15, 'Y').px(8, 15, 'Y').px(11, 15, 'Y'); // franja da toalha
  pix.rect(2, 16, 2, 3, 'G').rect(12, 16, 2, 3, 'G'); // pés
  return pix.outline('o');
}

/** Guarda-sol de praia visto de cima: lona em oito gomos vermelhos e brancos, haste e base pesada com sombra no chão. Ocupa 2×2 tiles. */
function guardaSol(): Pix {
  const pix = new Pix(32, 40);
  const cx = 16;
  const cy = 12;
  for (let y = 0; y < 32; y++) {
    for (let x = 0; x < 32; x++) {
      const nx = (x + 0.5 - cx) / 15;
      const ny = (y + 0.5 - cy) / 9; // a lona é elíptica: vista de 3/4
      const r = Math.hypot(nx, ny);
      if (r > 1) continue;
      const gomo = Math.floor(((Math.atan2(ny, nx) + Math.PI) / (2 * Math.PI)) * 8) % 2 === 0;
      if (r > 0.82) pix.px(x, y, gomo ? 'd' : 'v'); // borda da lona, em sombra
      else pix.px(x, y, gomo ? 'r' : 'w');
    }
  }
  pix.rect(15, 11, 2, 2, 'm'); // ponta da lona
  pix.vline(15, 21, 12, 'p').vline(16, 21, 12, 'm'); // haste
  pix.box(7, 31, 18, 4, { fill: 'M', light: 'm', dark: 'o' }); // base pesada: corpo
  elipse(pix, 16, 31, 9, 3, 'm'); // base: tampo
  pix.outline('o');
  // sombra no chão, só onde não há sprite (depois do contorno, para não ganhar borda)
  for (let y = 30; y < 40; y++) {
    for (let x = 0; x < 32; x++) {
      if (pix.get(x, y) === '.' && ((x + 0.5 - 16) / 14) ** 2 + ((y + 0.5 - 36) / 3.5) ** 2 <= 1) pix.px(x, y, 's');
    }
  }
  return pix;
}

/** Elevador: portas de metal fechadas, fenda no meio e painel de andar. Ocupa 2×1 tiles e vai na parede. */
function elevador(): Pix {
  const pix = new Pix(32, 18);
  pix.box(0, 0, 32, 18, { fill: 'm', light: 'l', dark: 'd' }); // moldura
  pix.box(3, 3, 12, 15, { fill: 'p', light: 'q', dark: 'r' }); // porta esquerda
  pix.box(17, 3, 12, 15, { fill: 'p', light: 'q', dark: 'r' }); // porta direita
  pix.vline(15, 3, 15, 'd').vline(16, 3, 15, 'd'); // fenda central
  pix.rect(13, 1, 6, 1, 's'); // painel de andar
  return pix.outline('o');
}

/** Vaso alto de chão com planta de folhas redondas. */
function vasoAlto(): Pix {
  const pix = new Pix(16, 30);
  pix.disc(8, 9, 7, 'l').disc(8, 11, 6, 'g').disc(4, 6, 3, 'g'); // copa
  pix.disc(11, 5, 3, 'l'); // folhas em destaque
  pix.box(3, 19, 10, 11, { fill: 'p', light: 'P', dark: 'q' }); // vaso
  return pix.outline('o');
}

export const COMMON_FURNITURE: readonly FurnitureDef[] = [
  {
    id: 'sofa',
    name: 'Sofá',
    category: 'lounge',
    footprint: { w: 2, h: 1 },
    sprite: sofa().sprite({ o: 'fabricBlue0', d: 'fabricBlue1', c: 'fabricBlue2', l: 'fabricBlue3', w: 'wood1' }),
    sortBias: -4,
    spots: [
      { dx: 0, dy: 0, facing: 'down', kind: 'sofa', use: 'rest' },
      { dx: 1, dy: 0, facing: 'down', kind: 'sofa', use: 'rest' },
    ],
  },

  // ---- copa
  {
    id: 'balcao-pia',
    name: 'Balcão com pia',
    category: 'kitchen',
    footprint: { w: 2, h: 1 },
    sprite: balcaoPia().sprite(BALCAO_PALETTE),
  },
  {
    id: 'balcao-cafeteira',
    name: 'Balcão com cafeteira',
    category: 'kitchen',
    footprint: { w: 1, h: 1 },
    sprite: cafeteira(0).sprite(CAFETEIRA_PALETTE),
    frames: [cafeteira(1).sprite(CAFETEIRA_PALETTE)],
    frameMs: 900,
    spots: [{ dx: 0, dy: -1, facing: 'down', kind: 'standing', use: 'eat' }],
  },
  {
    id: 'geladeira',
    name: 'Geladeira',
    category: 'kitchen',
    footprint: { w: 1, h: 1 },
    sprite: geladeira().sprite({ o: 'plastic0', p: 'plastic2', q: 'plastic3', r: 'plastic1', k: 'metal1' }),
  },
  {
    id: 'micro-ondas-balcao',
    name: 'Micro-ondas',
    category: 'kitchen',
    footprint: { w: 1, h: 1 },
    sprite: microondas().sprite({ o: 'plastic0', p: 'plastic2', q: 'plastic3', r: 'plastic1', k: 'black1', K: 'black2', g: 'light2' }),
  },
  {
    id: 'maquina-snacks',
    name: 'Máquina de snacks',
    category: 'kitchen',
    footprint: { w: 1, h: 1 },
    sprite: maquinaSnacks().sprite({ o: 'red0', R: 'red2', S: 'red3', D: 'red1', g: 'glass1', G: 'glass3', h: 'glass2', y: 'gold2', b: 'fabricBlue2', w: 'paper3', k: 'black1', K: 'metal3' }),
    glow: { color: 'rgba(255, 215, 106, 0.35)', radius: 22, dy: -4 },
  },
  {
    id: 'mesa-redonda',
    name: 'Mesa redonda',
    category: 'kitchen',
    footprint: { w: 2, h: 2 },
    sprite: mesaRedonda().sprite({ o: 'wood0', W: 'wood2', L: 'wood3', D: 'wood1' }),
    spots: [
      { dx: 0, dy: -1, facing: 'down', kind: 'chair', use: 'eat' },
      { dx: 1, dy: -1, facing: 'down', kind: 'chair', use: 'eat' },
      { dx: 0, dy: 2, facing: 'up', kind: 'chair', use: 'eat' },
      { dx: 1, dy: 2, facing: 'up', kind: 'chair', use: 'eat' },
    ],
  },
  {
    id: 'banqueta',
    name: 'Banqueta',
    category: 'kitchen',
    footprint: { w: 1, h: 1 },
    sprite: banqueta().sprite({ o: 'black0', d: 'red1', c: 'red2', l: 'red3', m: 'metal2', n: 'metal1' }),
  },
  {
    id: 'cadeira-madeira',
    name: 'Cadeira de madeira',
    category: 'kitchen',
    footprint: { w: 1, h: 1 },
    sprite: cadeiraMadeira().sprite({ o: 'wood0', W: 'wood2', L: 'wood3', D: 'wood1' }),
    sortBias: -4,
    blocks: false,
  },
  {
    id: 'mesa-cafe-retangular',
    name: 'Mesa de café',
    category: 'kitchen',
    footprint: { w: 3, h: 1 },
    sprite: mesaCafe().sprite({ o: 'wood0', W: 'wood2', L: 'wood3', D: 'wood1' }),
  },

  // ---- lounge
  {
    id: 'poltrona',
    name: 'Poltrona',
    category: 'lounge',
    footprint: { w: 1, h: 1 },
    sprite: poltrona().sprite({ o: 'fabricMustard0', c: 'fabricMustard2', l: 'fabricMustard3', d: 'fabricMustard1', k: 'wood1' }),
    sortBias: -4,
    spots: [{ dx: 0, dy: 0, facing: 'down', kind: 'sofa', use: 'rest' }],
  },
  {
    id: 'pufe',
    name: 'Pufe',
    category: 'lounge',
    footprint: { w: 1, h: 1 },
    sprite: pufe().sprite({ o: 'fabricGray0', d: 'fabricGray1', c: 'fabricGray2', l: 'fabricGray3' }),
    blocks: false,
  },
  {
    id: 'mesa-centro',
    name: 'Mesa de centro',
    category: 'lounge',
    footprint: { w: 2, h: 1 },
    sprite: mesaCentro().sprite({ o: 'wood0', G: 'glass2', g: 'glass3', h: 'glass1', D: 'wood1', p: 'paper2', q: 'paper1' }),
  },
  {
    id: 'mesa-pingpong',
    name: 'Mesa de ping-pong',
    category: 'lounge',
    footprint: { w: 3, h: 2 },
    sprite: pingPong().sprite({ o: 'grass0', G: 'grass2', g: 'grass3', h: 'grass1', w: 'paper3', n: 'white3', d: 'metal1' }),
  },
  {
    id: 'fliperama',
    name: 'Fliperama',
    category: 'lounge',
    footprint: { w: 1, h: 1 },
    sprite: fliperama(0).sprite({ o: 'black0', k: 'black2', K: 'black3', j: 'black1', y: 'gold2', Y: 'gold3', z: 'gold1', b: 'screen1', B: 'screen2', c: 'screen3', r: 'red2', g: 'leaf3' }),
    frames: [fliperama(1).sprite({ o: 'black0', k: 'black2', K: 'black3', j: 'black1', y: 'gold2', Y: 'gold3', z: 'gold1', b: 'screen1', B: 'screen2', c: 'screen3', r: 'red2', g: 'leaf3' })],
    frameMs: 700,
    glow: { color: 'rgba(134, 214, 255, 0.3)', radius: 18, dy: -6 },
  },
  {
    id: 'tapete-lounge',
    name: 'Tapete do lounge',
    category: 'lounge',
    footprint: { w: 3, h: 2 },
    sprite: tapete().sprite({ b: 'carpet0', c: 'carpet2', l: 'carpet3', d: 'carpet1' }),
    blocks: false,
    sortBias: -40,
  },
  {
    id: 'rack-tv',
    name: 'Rack de TV',
    category: 'lounge',
    footprint: { w: 2, h: 1 },
    sprite: rackTv().sprite({ o: 'wood0', W: 'wood2', L: 'wood3', D: 'wood1', k: 'black1', K: 'black3', j: 'black0', s: 'screen1', S: 'screen2', r: 'screen0', m: 'metal1' }),
  },
  {
    id: 'estante-games',
    name: 'Estante de jogos',
    category: 'lounge',
    footprint: { w: 1, h: 1 },
    sprite: estanteGames().sprite({ o: 'wood0', W: 'wood2', L: 'wood3', D: 'wood1', r: 'red2', b: 'fabricBlue2', g: 'fabricGreen2', y: 'fabricMustard2', p: 'fabricGray2' }),
  },
  {
    id: 'sofa-verde',
    name: 'Sofá verde',
    category: 'lounge',
    footprint: { w: 3, h: 1 },
    sprite: sofaTres().sprite(SOFA_VERDE),
    sortBias: -4,
    spots: [
      { dx: 0, dy: 0, facing: 'down', kind: 'sofa', use: 'rest' },
      { dx: 1, dy: 0, facing: 'down', kind: 'sofa', use: 'rest' },
      { dx: 2, dy: 0, facing: 'down', kind: 'sofa', use: 'rest' },
    ],
  },

  // ---- banheiro
  {
    id: 'vaso-sanitario',
    name: 'Vaso sanitário',
    category: 'restroom',
    footprint: { w: 1, h: 1 },
    sprite: vasoSanitario().sprite({ o: 'white0', w: 'white2', x: 'white3', y: 'white1', k: 'water1' }),
    spots: [{ dx: 0, dy: 0, facing: 'down', kind: 'chair', use: 'restroom' }],
  },
  {
    id: 'pia-banheiro',
    name: 'Pia de banheiro',
    category: 'restroom',
    footprint: { w: 1, h: 1 },
    sprite: piaBanheiro().sprite({ o: 'white0', w: 'white2', x: 'white3', y: 'white1', m: 'metal2', k: 'black1' }),
  },
  {
    id: 'divisoria-cabine',
    name: 'Divisória de cabine',
    category: 'restroom',
    footprint: { w: 1, h: 1 },
    sprite: divisoriaCabine().sprite({ o: 'plastic0', p: 'plastic2', q: 'plastic3', r: 'plastic1', m: 'metal2' }),
  },
  {
    id: 'espelho-banheiro',
    name: 'Espelho',
    category: 'restroom',
    footprint: { w: 1, h: 1 },
    sprite: espelhoBanheiro().sprite({ o: 'metal0', m: 'metal2', l: 'metal3', d: 'metal1', g: 'glass2', G: 'glass3', h: 'glass1' }),
    wall: true,
    blocks: false,
  },
  {
    id: 'secador-maos',
    name: 'Secador de mãos',
    category: 'restroom',
    footprint: { w: 1, h: 1 },
    sprite: secadorMaos().sprite({ o: 'plastic0', p: 'plastic2', q: 'plastic3', r: 'plastic1', k: 'black1' }),
    wall: true,
    blocks: false,
  },

  // ---- reunião
  {
    id: 'mesa-reuniao',
    name: 'Mesa de reunião',
    category: 'meeting',
    footprint: { w: 4, h: 2 },
    sprite: mesaReuniao().sprite({
      o: 'oak0',
      W: 'oak2',
      L: 'oak3',
      D: 'oak1',
      F: 'wood1',
      G: 'wood0',
      m: 'metal2',
      l: 'metal3',
      d: 'metal1',
      k: 'black2',
      K: 'black3',
      j: 'black1',
      q: 'paper2',
      p: 'paper3',
      r: 'paper1',
      z: 'paper0',
      c: 'plastic2',
      C: 'plastic3',
      y: 'plastic1',
      T: 'trunk1',
    }),
    spots: [
      { dx: 0, dy: -1, facing: 'down', kind: 'chair', use: 'meet' },
      { dx: 1, dy: -1, facing: 'down', kind: 'chair', use: 'meet' },
      { dx: 2, dy: -1, facing: 'down', kind: 'chair', use: 'meet' },
      { dx: 3, dy: -1, facing: 'down', kind: 'chair', use: 'meet' },
      { dx: 0, dy: 2, facing: 'up', kind: 'chair', use: 'meet' },
      { dx: 1, dy: 2, facing: 'up', kind: 'chair', use: 'meet' },
      { dx: 2, dy: 2, facing: 'up', kind: 'chair', use: 'meet' },
      { dx: 3, dy: 2, facing: 'up', kind: 'chair', use: 'meet' },
    ],
  },
  {
    id: 'cadeira-reuniao',
    name: 'Cadeira de reunião',
    category: 'meeting',
    footprint: { w: 1, h: 1 },
    sprite: cadeiraReuniao().sprite({ o: 'black0', c: 'fabricBlue2', l: 'fabricBlue3', d: 'fabricBlue1', m: 'metal1', n: 'metal2' }),
    sortBias: -4,
    blocks: false,
  },
  {
    id: 'tela-projecao',
    name: 'Tela de projeção',
    category: 'meeting',
    footprint: { w: 2, h: 1 },
    sprite: telaProjecao().sprite({ o: 'metal0', m: 'metal2', l: 'metal3', d: 'metal1', w: 'white2', x: 'white3', y: 'white1' }),
    wall: true,
    blocks: false,
  },

  // ---- recepção
  {
    id: 'balcao-recepcao',
    name: 'Balcão de recepção',
    category: 'reception',
    footprint: { w: 3, h: 1 },
    sprite: balcaoRecepcao().sprite({ o: 'red0', W: 'oak2', L: 'oak3', D: 'oak1', R: 'red2', S: 'red3', Q: 'red1', G: 'gold2' }),
    spots: [{ dx: 1, dy: -1, facing: 'down', kind: 'standing', use: 'work' }],
  },
  {
    id: 'sofa-espera',
    name: 'Sofá de espera',
    category: 'reception',
    footprint: { w: 2, h: 1 },
    sprite: sofa().sprite({ o: 'fabricMustard0', d: 'fabricMustard1', c: 'fabricMustard2', l: 'fabricMustard3', w: 'wood1' }),
    sortBias: -4,
    spots: [
      { dx: 0, dy: 0, facing: 'down', kind: 'sofa', use: 'wait' },
      { dx: 1, dy: 0, facing: 'down', kind: 'sofa', use: 'wait' },
    ],
  },
  {
    id: 'totem-agzos',
    name: 'Totem Agzos',
    category: 'reception',
    footprint: { w: 1, h: 1 },
    sprite: totem().sprite({ o: 'black0', R: 'red2', S: 'red3', D: 'red1', W: 'white3', p: 'plastic2', q: 'plastic3', r: 'plastic1', b: 'screen1', B: 'screen2', c: 'screen3', m: 'metal2', l: 'metal3', d: 'metal1' }),
  },

  // ---- servidores
  {
    id: 'rack-servidor',
    name: 'Rack de servidor',
    category: 'servers',
    footprint: { w: 1, h: 1 },
    sprite: rack(0).sprite({ o: 'black0', k: 'black2', K: 'black3', j: 'black1', f: 'metal1', F: 'metal2', g: 'leaf3', r: 'red2', b: 'screen2' }),
    frames: [rack(1).sprite({ o: 'black0', k: 'black2', K: 'black3', j: 'black1', f: 'metal1', F: 'metal2', g: 'leaf3', r: 'red2', b: 'screen2' })],
    frameMs: 500,
    glow: { color: 'rgba(124, 197, 106, 0.22)', radius: 20, dy: -6 },
    spots: [{ dx: 0, dy: 1, facing: 'up', kind: 'standing', use: 'server' }],
  },
  {
    id: 'no-break',
    name: 'No-break',
    category: 'servers',
    footprint: { w: 1, h: 1 },
    sprite: noBreak().sprite({ o: 'plastic0', p: 'plastic2', q: 'plastic3', r: 'plastic1', k: 'black1', g: 'leaf3' }),
  },
  {
    id: 'mesa-noc',
    name: 'Mesa de NOC',
    category: 'servers',
    footprint: { w: 2, h: 1 },
    sprite: mesaNoc().sprite({ o: 'black0', k: 'black2', K: 'black3', j: 'black1', s: 'screen2', c: 'screen3', m: 'metal1', W: 'oak2', L: 'oak3', D: 'oak1' }),
  },

  // ---- terraço e hall
  {
    id: 'espreguicadeira',
    name: 'Espreguiçadeira',
    category: 'exterior',
    footprint: { w: 1, h: 1 },
    sprite: espreguicadeira().sprite({
      o: 'oak0',
      L: 'oak3',
      D: 'oak2',
      G: 'oak1',
      c: 'fabricBlue2',
      l: 'fabricBlue3',
      w: 'white2',
      Y: 'fabricMustard2',
      Z: 'fabricMustard3',
      y: 'fabricMustard1',
    }),
    blocks: false,
  },
  {
    id: 'guarda-sol',
    name: 'Guarda-sol',
    category: 'exterior',
    footprint: { w: 2, h: 2 },
    sprite: guardaSol().sprite({ o: 'black0', r: 'red2', d: 'red1', w: 'white3', v: 'white1', m: 'metal2', p: 'metal3', M: 'metal1', s: 'shadow' }),
  },
  {
    id: 'elevador',
    name: 'Elevador',
    category: 'wall',
    footprint: { w: 2, h: 1 },
    sprite: elevador().sprite({ o: 'metal0', m: 'metal2', l: 'metal3', d: 'metal1', p: 'plastic2', q: 'plastic3', r: 'plastic1', s: 'screen3' }),
    wall: true,
    blocks: false,
  },
  {
    id: 'vaso-alto',
    name: 'Vaso alto',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    sprite: vasoAlto().sprite({ o: 'leaf0', l: 'leaf3', g: 'leaf2', p: 'pot2', P: 'pot3', q: 'pot1' }),
  },
];
